import { useState, useEffect } from 'react';
import { localeUI } from '../lib/format';
import { CalendarDays, Camera, Check, ClipboardList, HardHat, Mail, MapPin, MessagesSquare, RotateCcw, Timer, Users } from 'lucide-react';
import { InspectorFilesBox } from './InspectorFilesBox';
import { InspectorNotesFeed } from './InspectorNotesFeed';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useScrollOnOpen } from '../lib/useScrollOnOpen';
import { FlowBadges, daysAt, type FlowRow } from './AcceptedFlowShared';
import { ClaimChat } from './ClaimChat';
import { ClaimEmailBox } from './ClaimEmailBox';
import { ScheduleModal } from './ScheduleModal';
import { BulkRowCheck } from '../lib/bulkSelect';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';
import { DecisionBar } from './ui/DecisionBar';

/** Acciones de la cola "Get reinspection" (Claims llamando al seguro): cuando el adjuster
 *  aprueba, pasa a Scheduling; o si dio la cita EN LA LLAMADA, se agenda aquí mismo.
 *  Aprobar MUEVE el claim → DecisionBar con sólido verde (rediseño). */
function GetReinspectionActions({ claimId, clientName, onChanged }: { claimId: string; clientName?: string | null; onChanged?: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  async function approve() {
    if (!window.confirm(t('cwa.reinspApprovedConfirm'))) return;
    setBusy(true);
    try { await api.post(`/api/claims/${claimId}/claims-decision`, { decision: 'reinspection_approved' }); onChanged?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  return (
    <div className="module">
      <DecisionBar label={<><RotateCcw size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cwa.getReinspTitle')}</>}>
        <button className="btn sm solid-good" disabled={busy} onClick={approve}>
          <Check size={14} strokeWidth={1.75} /> {t('cwa.reinspApproved')}
        </button>
        <button className="btn sm" disabled={busy} style={{ background: 'var(--green-soft)', color: 'var(--green)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }} onClick={() => setScheduling(true)}>
          <CalendarDays size={14} strokeWidth={1.75} /> {t('cr.schedule')}
        </button>
      </DecisionBar>
      {scheduling && (
        <ScheduleModal claimId={claimId} title={clientName ?? undefined} defaultType="adjuster_meeting"
          onClose={() => setScheduling(false)} onDone={() => { setScheduling(false); onChanged?.(); }} />
      )}
    </div>
  );
}

/**
 * Insurance inspection: notas para el inspector (editable) + botón de fotos. No RCV.
 *
 * Las notas eran SOLO texto, y el caso que lo destapó es el ATR: hay que mandarle al inspector
 * la foto de referencia y no había dónde ponerla — se subía a Files y el inspector, que en la
 * app de campo solo recibe las notas, nunca la veía. Ahora lo que se suelte acá viaja con la
 * visita: queda en Files del claim como cualquier documento, pero marcado "para el inspector".
 */
function InspectorNotesBox({ claimId, photosUrl }: { claimId: string; photosUrl?: string | null }) {
  const { t } = useI18n();
  const [preview, setPreview] = useState('');
  useEffect(() => {
    api.get<{ inspectorNotes: string }>(`/api/claims/${claimId}/scheduling-info`)
      .then((r) => setPreview(r.inspectorNotes || '')).catch(() => {});
  }, [claimId]);

  return (
    <Module id={`inspector-notes:${claimId}`} icon={ClipboardList} title={t('cwa.inspectorNotes')}
      chip={(
        <span style={{ marginLeft: 'auto', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
          {photosUrl
            ? <a href={photosUrl} target="_blank" rel="noopener noreferrer" className="btn sm"><Camera size={14} strokeWidth={1.75} /> {t('cwa.photos')}</a>
            : <RChip icon={Camera}>{t('cwa.noPhotos')}</RChip>}
        </span>
      )}
      preview={preview ? preview.slice(0, 140) : t('cwa.inspectorNotesPh')}>
      <div style={{ display: 'grid', gap: 8, paddingTop: 2 }}>
        {/* Lista de notas con autor y hora (Manuel 2026-10-08): cada Save agrega una y se ve que se envió. */}
        <InspectorNotesFeed claimId={claimId} placeholder={t('cwa.inspectorNotesPh')} />

        {/* Adjuntos que el inspector ve en el celular junto a estas notas. */}
        <InspectorFilesBox claimId={claimId} />
      </div>
    </Module>
  );
}

function GenericRow({ r, onClient, onChanged }: { r: FlowRow; onClient: (id: string) => void; onChanged?: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rowRef = useScrollOnOpen<HTMLDivElement>(open);
  const days = daysAt(r.status_since);
  // Insurance inspection: no importa "días en status" ni follow-up; importa la CITA agendada
  // (fecha) y el INSPECTOR que asiste.
  const isInspection = r.status === 'Insurance inspection';
  const apptDate = r.scheduled_start ? new Date(r.scheduled_start).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <div ref={rowRef} className="card" style={{ padding: 12, scrollMarginTop: 80 }}>
      {/* Ficha recogida en 2 líneas densas (pedido Claim in). */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {/* Selección múltiple: solo aparece en las vistas que la habilitan (BulkProvider). */}
        <span style={{ marginTop: 3 }}><BulkRowCheck id={r.claim_id} /></span>
        <button onClick={() => setOpen((o) => !o)} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, marginTop: 2, font: 'inherit' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Línea 1: nombre · claim# · seguro (sin emoji) · contratista · días en status */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => onClient(r.claim_id)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--ink)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
              {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
            </button>
            {r.claim_number && <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', textDecoration: 'none' }}>{r.claim_number}</Link>}
            {r.insurance_company && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--bg)', color: 'var(--ink)' }}>{r.insurance_company}</span>}
            {r.contractor_name && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· {r.contractor_name}</span>}
            {!isInspection && days != null && <RChip variant={days >= 7 ? 'no' : 'info'} icon={Timer} title={t('fu.daysInStage')}>{days}d</RChip>}
          </div>
          {/* Línea 2: dirección · team · fotos · badges. Insurance inspection: cita + inspector
              (sin follow-up); resto: welcome/intake/contract + fecha de pérdida. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {r.address && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</span>}
            <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--accent-soft)', color: 'var(--accent)' }}><Users size={12} strokeWidth={1.75} /> {r.assigned_team_name || '—'}</span>
            {r.photos_url
              ? <a href={r.photos_url} target="_blank" rel="noopener noreferrer" className="btn ghost sm" onClick={(e) => e.stopPropagation()}><Camera size={14} strokeWidth={1.75} /> {t('cwa.photos')}</a>
              : <RChip icon={Camera}>{t('cwa.noPhotos')}</RChip>}
            <FlowBadges r={r} showDays={false} showFollowUp={!isInspection} />
            {isInspection ? (
              <>
                <RChip variant={apptDate ? 'ok' : 'warn'} icon={CalendarDays} title={t('cr.schedule')}>{apptDate || t('cwa.noAppt')}</RChip>
                <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--bg)', color: 'var(--ink)' }}><HardHat size={12} strokeWidth={1.75} /> {r.scheduled_inspector || t('cwa.noInspector')}</span>
              </>
            ) : (
              <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--bg)', color: 'var(--ink)' }}><CalendarDays size={12} strokeWidth={1.75} /> {t('cdw.dateOfLoss')}: {r.date_of_loss || '—'}</span>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 12, display: 'grid', gap: 12 }}>
          {/* Cola "Get reinspection": aprobar (→ Scheduling) o agendar directo si el adjuster dio la cita. */}
          {r.status === 'Re-inspection request/ed' && (
            <GetReinspectionActions claimId={r.claim_id} clientName={r.homeowner_name} onChanged={onChanged} />
          )}
          {/* Insurance inspection: notas para el inspector + fotos (en vez de RCV). */}
          {isInspection && <InspectorNotesBox claimId={r.claim_id} photosUrl={r.photos_url} />}
          {/* Follow-up unificado en Department Updates (ClaimChat) — módulos del rediseño,
              recogidos por defecto (pedido del dueño: abierto empujaba todo hasta abajo). */}
          <Module id={`updates:${r.claim_id}`} icon={MessagesSquare} title={t('cwa.updates')} preview={t('mod.updatesPreview')} defaultOpen={false}>
            <ClaimChat claimId={r.claim_id} channel="claims" title={t('cwa.updates')} />
          </Module>
          <Module id={`email-overview:${r.claim_id}`} icon={Mail} title={t('cwa.emailOverview')} preview={t('mod.emailPreview')} defaultOpen={false}>
            <ClaimEmailBox claimId={r.claim_id} departmentKey="claims" />
          </Module>
        </div>
      )}
    </div>
  );
}

/** Cola genérica del depto de Claims (Claim in, HOLD, Approved, etc.): filas consistentes de
 *  oversight, SIEMPRE a ancho completo (estándar fuerte para todas las colas de Claims). */
export function ClaimsGenericSection(
  { rows, onClient, onChanged }: { rows: FlowRow[]; onClient: (id: string) => void; view?: 'cards' | 'lines'; onChanged?: () => void },
) {
  const { t } = useI18n();
  if (rows.length === 0) return <div className="page-empty">{t('dept.noClaims')}</div>;
  return <div style={{ display: 'grid', gap: 10, width: '100%' }}>{rows.map((r) => <GenericRow key={r.presence_id} r={r} onClient={onClient} onChanged={onChanged} />)}</div>;
}
